// The store peek (iOS RecordPeekSheet, WEB_PLAN.md §5.7 "Store peek"): the selected pin's StoreRow floating over the
// map with Open (→ /stores/:id). Phase 1 is read-only, so Assign and Change stage come with Phase 2. Escape closes it.

import { ExternalLink, X } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import type { RecordPayload } from '@/contracts/models';
import type { DataItem } from '@/data/hooks';
import { StoreRow, type StoreRowLookups } from '@/features/common';
import { Button, cn, isModalOpen } from '@/ui';

export interface StorePeekProps {
  item: DataItem<RecordPayload>;
  lookups: StoreRowLookups;
  onClose: () => void;
  className?: string;
}

export function StorePeek({ item, lookups, onClose, className }: StorePeekProps) {
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Leave Escape to an open menu, search field or dialog; only close when nothing else claims it.
      if (e.key === 'Escape' && !e.defaultPrevented && !isModalOpen()) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <section aria-label="Selected store" className={cn('pointer-events-auto w-[360px] max-w-full animate-pop-in', className)}>
      <StoreRow
        item={item}
        lookups={lookups}
        variant="compact"
        showStage
        className="shadow-pop"
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" icon={<ExternalLink aria-hidden />} onClick={() => navigate(`/stores/${item.id}`)}>
              Open
            </Button>
            <Button size="sm" variant="ghost" icon={<X aria-hidden />} onClick={onClose}>
              Close
            </Button>
          </div>
        }
      />
    </section>
  );
}
